import {
  Box,
  ButtonBase,
  IconButton,
  LinearProgress,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { selectScreen, setLoading } from "redux/appSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import history from "utils/history";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupsIcon from "@mui/icons-material/Groups";
import { SignRequest } from "models/interfaces";
import {
  getSignRequestData,
  getUnsignedDocumentUrl,
} from "../MySignRequestService";
import { downloadSignedDocument } from "screens/RenewableDocuments/RenewableService";
import { getStatusColor, handleErrorMessage, isTruthy } from "helpers/methods";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import ParticipantDocumentViewer from "screens/SignRequest/ParticipantScreen/ParticipantDocumentViewer";
import { fieldColor } from "screens/SignRequest/SignRequestHelper";
import { RootState } from "utils/store";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import { fetchDocumentDimensions } from "screens/SignRequest/SignRequestService";
import { getSignerLabel } from "../SignRequestsRowHelper";

/** Shown instead of a file name when the viewer is holding the single merged PDF. */
const MERGED_DOC_LABEL = "Signed document";

const ViewDocuments = ({
  request,
  initialDocIndex = 0,
  fillHeight = false,
  hideToolbar = false,
}: {
  request: any;
  initialDocIndex?: number;
  /** Fill the parent instead of the modal's fixed 82vh, for a caller that sizes its own columns. */
  fillHeight?: boolean;
  /**
   * Drop the document selector and the signer summary, leaving the page alone. Off by default, so the
   * Preview modal and every other caller keep the toolbar exactly as it was.
   */
  hideToolbar?: boolean;
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isMobile = useAppSelector(selectScreen);
  // const redirectedState: any = history?.location?.state;
  const urlParams = useLocation().search;
  const paramsIdFromUrl: any = new URLSearchParams(urlParams).get("id");
  const [fileUrls, setFileUrls] = useState([]);
  const [selectedFileData, setSelectedFileData] = useState<any>(null);
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);
  const [viewerDocLoading, setViewerDocLoading] = useState(false);
  const [viewerDocLoadSuccess, setViewerDocLoadSuccess] = useState(false);
  const [docsAnchorEl, setDocsAnchorEl] = useState<null | HTMLElement>(null);
  const [signersAnchorEl, setSignersAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const { loading, isRequest } = useAppSelector(
    (state: RootState) => state.app,
  );
  const defaultSignRequest = {
    id: "",
    signers: [],
    files: [],
    expirationDays: 0,
    emailSubject: "",
    emailMessage: "",
    requestedBy: { name: "", email: "" },
    signedBy: "",
    status: "",
    approval: {
      approvalNeeded: false,
      orderNeeded: false,
      reviewers: [
        {
          id: 0,
          name: "",
          email: "",
          order: 0,
          color: "",
          status: "",
          group: "",
        },
      ],
    },
    signerOrderRequired: false,
    selfSigned: false,
    templateName: "",
    imagePath: "",
    scheduleTime: "",
    reminders: null,
    needReminders: false,
    createdOn: "",
    signRequestConfig: {
      attachDocumentToMailOnCompletion: false,
      allowDocumentDownload: false,
      sendNotificationOnCompletion: false,
      attachSingleCertificate: false,
    },
    currentSigners: [],
    requestToken: "",
    autoGeneratedId: 0,
    templateId: null,
  };

  const [signRequest, setSignRequest] = useState<SignRequest>(
    isTruthy(request)
      ? structuredClone(request)
      : structuredClone(defaultSignRequest),
  );

  const isSignAccess = hasAccessTo(
    strings.resources.sign,
    strings.fetchPermission,
  );

  const isTemplateAccess = hasAccessTo(
    strings.resources.template,
    strings.fetchPermission,
  );

  const fetchSignRequest = useCallback(async () => {
    if ((isRequest && !isSignAccess) || (!isRequest && !isTemplateAccess)) {
      return;
    }
    try {
      dispatch(setLoading(true));
      const data = await getSignRequestData(request.id);
      // When at least one signer has signed, the backend has a partially-signed
      // PDF whose contents have those signers' values rendered into the file
      // itself (correct positions, real signature images). Prefer that PDF so
      // we don't have to overlay signed values ourselves — the remaining
      // pending signers' empty placeholders still overlay via the existing
      // pending/delay filter in ParticipantDocumentViewer.
      const anySignerSigned = (data.signers || []).some(
        (s: any) => (s.status || "").toLowerCase() === "signed",
      );
      // Once the request is complete, an account that merges signed files into
      // one certificated PDF makes the backend return that SAME merged document
      // for every file. Fetching each file would download the identical PDF N
      // times and list N switcher entries that all render the same content.
      const mergedOnly =
        Boolean(data.signRequestConfig?.attachSingleCertificate) &&
        (data.status === strings.signed || data.status === strings.completed);
      const filesToShow = mergedOnly ? data.files.slice(0, 1) : data.files;
      const fileUrls: any = [];
      for (const file of filesToShow) {
        try {
          const useSignedPdf = anySignerSigned && Boolean(file.signedPdf);
          const [urlValue, dimensionResponse] = await Promise.all([
            useSignedPdf
              ? downloadSignedDocument(request.id!, file.signedPdf).then(
                  async (res) => URL.createObjectURL(await res.blob()),
                )
              : getUnsignedDocumentUrl(file, isRequest, request.id!).then(
                  (r) => r.message,
                ),
            fetchDocumentDimensions(
              { unsignedPdf: file.unsignedPdf || file.unsignedDoc },
              isRequest,
              request.id!,
              false,
            ).catch(() => undefined),
          ]);
          fileUrls.push({
            fileName: mergedOnly
              ? MERGED_DOC_LABEL
              : useSignedPdf
                ? file.signedPdf
                : file.unsignedPdf || file.signedPdf,
            url: urlValue,
            isSignedPdf: useSignedPdf,
            file: {
              ...file,
              dimensions: dimensionResponse || file.dimensions || {},
            },
          });
        } catch (error) {
          console.error(
            `Error fetching URL for file: ${file.unsignedPdf}`,
            error,
          );
        }
      }
      setSignRequest(data);
      setFileUrls(fileUrls);
      if (fileUrls.length > 0) {
        const startIndex =
          initialDocIndex >= 0 && initialDocIndex < fileUrls.length
            ? initialDocIndex
            : 0;
        setSelectedFileData(fileUrls[startIndex]);
        setSelectedDocIndex(startIndex);
      }
      dispatch(setLoading(false));
    } catch (error: any) {
      dispatch(setLoading(false));
      handleErrorMessage(error);
    }
  }, [
    dispatch,
    isRequest,
    isSignAccess,
    isTemplateAccess,
    request.id,
    initialDocIndex,
  ]);

  useEffect(() => {
    if ((isRequest && !isSignAccess) || (!isRequest && !isTemplateAccess)) {
      return;
    }

    fetchSignRequest();
  }, [fetchSignRequest, isRequest, isSignAccess, isTemplateAccess]);

  // Revoke any blob URLs created for signed-PDF downloads when fileUrls change
  // or the component unmounts so we don't leak object URLs.
  useEffect(() => {
    return () => {
      fileUrls.forEach((f: any) => {
        if (
          f?.isSignedPdf &&
          typeof f.url === "string" &&
          f.url.startsWith("blob:")
        ) {
          URL.revokeObjectURL(f.url);
        }
      });
    };
  }, [fileUrls]);

  const handleFileClick = (fileData: any, index: number) => {
    setSelectedFileData(fileData);
    setSelectedDocIndex(index);
  };

  const handlePrevDoc = () => {
    if (selectedDocIndex > 0) {
      handleFileClick(fileUrls[selectedDocIndex - 1], selectedDocIndex - 1);
    }
  };

  const handleNextDoc = () => {
    if (selectedDocIndex < fileUrls.length - 1) {
      handleFileClick(fileUrls[selectedDocIndex + 1], selectedDocIndex + 1);
    }
  };

  const signers = signRequest?.signers || [];
  const signedCount = signers.filter(
    (s: any) => (s.status || "").toLowerCase() === "signed",
  ).length;
  const totalSigners = signers.length;
  const progressPct = totalSigners > 0 ? (signedCount / totalSigners) * 100 : 0;
  const totalDocs = fileUrls.length;
  const currentName = selectedFileData?.fileName || "";
  const primary = theme.palette.primary.main;
  const isFirst = selectedDocIndex === 0;
  const isLast = selectedDocIndex === totalDocs - 1;

  const pdfViewer = selectedFileData ? (
    <ParticipantDocumentViewer
      key={selectedFileData.url}
      docLoading={viewerDocLoading}
      setDocLoading={setViewerDocLoading}
      docLoadSuccess={viewerDocLoadSuccess}
      setDocLoadSuccess={setViewerDocLoadSuccess}
      signerSelectedFile={{
        ...selectedFileData.file,
        unsignedPdfUrl: selectedFileData.url,
      }}
      setSignerSelectedFile={() => {}}
      loading={false}
      fileSrc={selectedFileData}
      isMobileScreen={isMobile}
      state={{
        signRequest: {
          ...signRequest,
          files: [selectedFileData.file],
        },
        signImage: "",
        initialsImage: "",
        validFields: {},
      }}
      dispatch={() => {}}
      setLoading={() => {}}
      selectedFile={{
        ...selectedFileData.file,
        unsignedPdfUrl: selectedFileData.url,
      }}
      setSelectedFile={() => {}}
      currentPageNumber={0}
      setCurrentPageNumber={() => {}}
      handleFileChange={() => {}}
      handleSubmit={() => {}}
      downloadFile={() => {}}
      currentReviewer={{
        id: 0,
        name: "",
        email: "",
        order: 0,
        color: "",
      }}
      handleApproveRejectRequest={() => {}}
      reqToken={""}
      currentFieldOnFocus={0}
      setCurrentFieldOnFocus={() => {}}
      moveToFieldLocation={() => {}}
      signingJourneyOn={false}
      setSigningJourneyOn={() => {}}
      attachSingleCertificate={false}
      allFieldsFilled={false}
      disableFields={true}
    />
  ) : (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1.5}
      sx={{ height: "100%" }}
    >
      <FolderOpenIcon
        sx={{ fontSize: 56, color: "text.disabled", opacity: 0.5 }}
      />
      <Typography variant="subtitle1" color="text.secondary">
        No Document Selected
      </Typography>
    </Stack>
  );

  const navArrowSx = (disabled: boolean) => ({
    width: 34,
    height: 34,
    borderRadius: 1.25,
    color: disabled ? "text.disabled" : "text.primary",
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: disabled ? "divider" : "#e5e7eb",
    transition: "all 0.15s ease",
    "&:hover": {
      bgcolor: disabled ? "background.paper" : `${primary}08`,
      borderColor: disabled ? "divider" : `${primary}66`,
      color: disabled ? "text.disabled" : primary,
    },
    "&.Mui-disabled": {
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: fillHeight ? "100%" : "82vh",
        minHeight: 0,
        bgcolor: "#f5f6f8",
        borderRadius: fillHeight ? 0 : 2,
        overflow: "hidden",
      }}
    >
      {loading ? (
        <CustomLoader isLoading={loading} />
      ) : (
        <>
          {/* Toolbar */}
          {!hideToolbar && (
          <Box
            sx={{
              px: { xs: 1.5, md: 2 },
              py: 1,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: { xs: "center", md: "space-between" },
              "& > *": {},
            }}
          >
            {/* Doc selector group */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                flexShrink: 0,
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <IconButton
                size="small"
                onClick={handlePrevDoc}
                disabled={isFirst || totalDocs === 0}
                sx={navArrowSx(isFirst || totalDocs === 0)}
                aria-label="Previous Document"
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <ButtonBase
                onClick={(e) => setDocsAnchorEl(e.currentTarget)}
                disabled={totalDocs === 0}
                aria-label="Select Document"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pl: 1,
                  pr: 1.25,
                  height: 34,
                  borderRadius: 1.25,
                  border: "1px solid",
                  borderColor: "#e5e7eb",
                  bgcolor: "background.paper",
                  transition: "all 0.15s ease",
                  minWidth: 0,
                  maxWidth: { xs: 200, sm: 320, md: 460 },
                  justifyContent: "center",
                  width: { xs: "100%", sm: "auto" },
                  "&:hover": {
                    borderColor: `${primary}66`,
                    boxShadow: `0 1px 3px ${primary}1f`,
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${primary}`,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: primary,
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  <PictureAsPdfIcon sx={{ fontSize: 14 }} />
                </Box>
                <Tooltip title={currentName || ""} arrow describeChild>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      lineHeight: 1,
                      color: "text.primary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                      flex: 1,
                      textAlign: "left",
                    }}
                  >
                    {currentName || "No document"}
                  </Typography>
                </Tooltip>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    pl: 0.75,
                    borderLeft: "1px solid",
                    borderColor: "#e5e7eb",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "text.secondary",
                      lineHeight: 1,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {totalDocs > 0
                      ? `${selectedDocIndex + 1}/${totalDocs}`
                      : "0/0"}
                  </Typography>
                  <KeyboardArrowDownIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                </Box>
              </ButtonBase>
              <IconButton
                size="small"
                onClick={handleNextDoc}
                disabled={isLast || totalDocs === 0}
                sx={navArrowSx(isLast || totalDocs === 0)}
                aria-label="Next Document"
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Stack>

            {/* Spacer */}
            {/* <Box sx={{ flex: 1 }} /> */}

            {/* Signers group */}
            {totalSigners > 0 && (
              <ButtonBase
                onClick={(e) => setSignersAnchorEl(e.currentTarget)}
                aria-label="View Signers"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  transition: "all 0.15s ease",
                  justifyContent: "center",
                  maxWidth: { xs: 274, md: "none" },
                  width: { xs: "100%", md: "auto" },
                  "&:hover": {
                    bgcolor: "grey.50",
                    borderColor: `${primary}66`,
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${primary}`,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: primary,
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 18 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      color: "text.primary",
                      lineHeight: 1,
                    }}
                  >
                    Signers
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: primary,
                      lineHeight: 1,
                      px: 0.75,
                      py: 0.375,
                      borderRadius: "999px",
                      bgcolor: `${primary}14`,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {signedCount}/{totalSigners}
                  </Typography>
                </Box>
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 18, color: "text.secondary" }}
                />
              </ButtonBase>
            )}
          </Box>
          )}

          {/* Thin progress strip */}
          {!hideToolbar && totalSigners > 0 && (
            <LinearProgress
              variant="determinate"
              value={progressPct}
              aria-label="Signer progress"
              sx={{
                height: 2,
                bgcolor: "grey.200",
                "& .MuiLinearProgress-bar": { bgcolor: primary },
              }}
            />
          )}

          {/* PDF viewer — full bleed */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              bgcolor: "#eef1f5",
              overflow: "auto",
              position: "relative",
            }}
          >
            {selectedFileData ? (
              <ParticipantDocumentViewer
                key={selectedFileData.url}
                docLoading={viewerDocLoading}
                setDocLoading={setViewerDocLoading}
                docLoadSuccess={viewerDocLoadSuccess}
                setDocLoadSuccess={setViewerDocLoadSuccess}
                signerSelectedFile={{
                  ...selectedFileData.file,
                  unsignedPdfUrl: selectedFileData.url,
                }}
                setSignerSelectedFile={() => {}}
                loading={false}
                fileSrc={selectedFileData}
                isMobileScreen={isMobile}
                state={{
                  signRequest: {
                    ...signRequest,
                    files: [selectedFileData.file],
                  },
                  signImage: "",
                  initialsImage: "",
                  validFields: {},
                }}
                dispatch={() => {}}
                setLoading={() => {}}
                selectedFile={{
                  ...selectedFileData.file,
                  unsignedPdfUrl: selectedFileData.url,
                }}
                setSelectedFile={() => {}}
                currentPageNumber={0}
                setCurrentPageNumber={() => {}}
                handleFileChange={() => {}}
                handleSubmit={() => {}}
                downloadFile={() => {}}
                currentReviewer={{
                  id: 0,
                  name: "",
                  email: "",
                  order: 0,
                  color: "",
                }}
                handleApproveRejectRequest={() => {}}
                reqToken={""}
                currentFieldOnFocus={0}
                setCurrentFieldOnFocus={() => {}}
                moveToFieldLocation={() => {}}
                signingJourneyOn={false}
                setSigningJourneyOn={() => {}}
                attachSingleCertificate={false}
                allFieldsFilled={false}
                disableFields={true}
              />
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1.5}
                sx={{ height: "100%" }}
              >
                <FolderOpenIcon
                  sx={{ fontSize: 56, color: "text.disabled", opacity: 0.5 }}
                />
                <Typography variant="subtitle1" color="text.secondary">
                  No Document Selected
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Docs popover */}
          <Popover
            anchorEl={docsAnchorEl}
            open={Boolean(docsAnchorEl)}
            onClose={() => setDocsAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 0.75,
                width: 360,
                maxWidth: "calc(100vw - 32px)",
                maxHeight: 480,
                borderRadius: 2,
                boxShadow: "0 16px 40px rgba(16,24,40,0.14)",
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
            }}
          >
            {/* List */}
            {/* <Box sx={{ flex: 1, overflowY: "auto" }}> */}
            <MenuList autoFocusItem={true} sx={{ py: 0, outline: "none" }}>
              {fileUrls.map((fileData: any, idx: number) => {
                const isSelected = idx === selectedDocIndex;
                return (
                  // <ButtonBase
                  <MenuItem
                    key={idx}
                    selected={isSelected}
                    autoFocus={isSelected}
                    onClick={() => {
                      handleFileClick(fileData, idx);
                      setDocsAnchorEl(null);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: 1,
                      px: 2,
                      py: 1.25,
                      transition: "background-color 0.15s ease",
                      "&.Mui-selected": {
                        bgcolor: `${primary}10`,
                        "&:hover": {
                          bgcolor: `${primary}18`,
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        width: 20,
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}.
                    </Typography>
                    <Tooltip
                      title={fileData.fileName || ""}
                      arrow
                      placement="top"
                      describeChild
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? "primary.main" : "text.primary",
                          fontSize: "0.85rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {fileData.fileName}
                      </Typography>
                    </Tooltip>
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{ fontSize: 16, color: primary, flexShrink: 0 }}
                      />
                    )}
                  </MenuItem>
                );
              })}
              {/* </Box> */}
            </MenuList>
          </Popover>

          {/* Signers popover */}
          <Popover
            anchorEl={signersAnchorEl}
            open={Boolean(signersAnchorEl)}
            onClose={() => setSignersAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 0.5,
                width: 320,
                maxWidth: "calc(100vw - 32px)",
                maxHeight: 440,
                borderRadius: 1.5,
                boxShadow: "0 12px 32px rgba(16,24,40,0.12)",
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {signers.map((signer: any) => {
                const color =
                  fieldColor[signer.id] || theme.palette.primary.main;
                const statusColor = getStatusColor(signer.status ?? "All");
                return (
                  <Stack
                    key={signer.id}
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{
                      px: 2,
                      py: 1,
                      transition: "background-color 0.15s ease",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 0.75,
                        bgcolor: color,
                        flexShrink: 0,
                        border: "1px solid rgba(0,0,0,0.08)",
                      }}
                    />
                    {/*
                      An in-person signer carries no name until the host
                      captures it at handover, so this falls back to the email
                      and then to the delivery mode rather than rendering blank.
                    */}
                    <Tooltip title={getSignerLabel(signer)} arrow describeChild>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.84rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {getSignerLabel(signer)}
                      </Typography>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{
                        color: statusColor,
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        textTransform: "capitalize",
                        flexShrink: 0,
                      }}
                    >
                      {signer.status || "—"}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

export default ViewDocuments;
